import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTenant } from '../lib/tenant-context';
import { tenantFetch } from '../lib/api-client';

interface Program {
  id: string;
  name: string;
  description: string | null;
  type: 'takeback' | 'trade_in' | 'resale';
  status: 'draft' | 'active' | 'paused' | 'archived';
  config: Record<string, unknown>;
  external_system: 'tes' | 'agentic' | string | null;
  external_id: string | null;
  created_at: string;
  updated_at: string;
  rules: Array<{
    id: string;
    rule_type: string;
    name: string;
    conditions: Record<string, unknown>;
    actions: Record<string, unknown>;
    priority: number;
    is_active: number;
  }>;
}

// SVG Icons - Clean, geometric, Pentatonic style
const TakebackIcon = ({ size = 24, color = '#171717' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2" strokeLinecap="round"/>
    <path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22" strokeLinecap="round" strokeDasharray="3 3"/>
    <path d="M8 12L12 8L16 12" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8V16" strokeLinecap="round"/>
  </svg>
);

const TradeInIcon = ({ size = 24, color = '#171717' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <path d="M7 10L3 14L7 18" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 14H3" strokeLinecap="round"/>
    <path d="M17 6L21 10L17 14" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 10H21" strokeLinecap="round"/>
  </svg>
);

const ResaleIcon = ({ size = 24, color = '#171717' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="7" cy="7" r="1.5" fill={color}/>
  </svg>
);

const BoxIcon = ({ size = 24, color = '#171717' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <path d="M21 16V8C20.9996 7.6493 20.9071 7.3048 20.7315 7.00017C20.556 6.69554 20.3037 6.44148 20 6.264L13 2.264C12.696 2.08619 12.3511 1.99219 12 1.99219C11.6489 1.99219 11.304 2.08619 11 2.264L4 6.264C3.69626 6.44148 3.44398 6.69554 3.26846 7.00017C3.09294 7.3048 3.00036 7.6493 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9998C3.44398 17.3045 3.69626 17.5585 4 17.736L11 21.736C11.304 21.9138 11.6489 22.0078 12 22.0078C12.3511 22.0078 12.696 21.9138 13 21.736L20 17.736C20.3037 17.5585 20.556 17.3045 20.7315 16.9998C20.9071 16.6952 20.9996 16.3507 21 16Z" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 12L21 7" strokeLinecap="round"/>
    <path d="M12 12L3 7" strokeLinecap="round"/>
    <path d="M12 12V22" strokeLinecap="round"/>
  </svg>
);

const AlertIcon = ({ size = 48, color = '#A9A9A9' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 8V12" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1" fill={color}/>
  </svg>
);

export const Route = createFileRoute('/programs_/$id')({
  component: ProgramDetail,
});

interface ProgramAnalytics {
  connected: boolean;
  error?: string;
  message?: string;
  tesClientId?: string;
  metrics: {
    total: number;
    inPipeline: number;
    shipped: number;
    delivered: number;
    processed: number;
    totalWeight: number;
    totalProducts: number;
    rewardsIssued: number;
    rewardsAmount: number;
    statusBreakdown: Record<string, number>;
    // Resale specific
    totalListings?: number;
    activeListings?: number;
    totalSales?: number;
    totalOrders?: number;
    totalItems?: number;
    itemsSold?: number;
    itemsShipped?: number;
    itemsDelivered?: number;
    gmv?: number;
    conversionRate?: number;
    dailyEvents?: Array<{ date: string; events: number }>;
  };
}

function ProgramDetail() {
  const { id } = Route.useParams() as { id: string };
  const [program, setProgram] = useState<Program | null>(null);
  const [analytics, setAnalytics] = useState<ProgramAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [period, setPeriod] = useState('30d');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [tesClientId, setTesClientId] = useState('');
  const [tesApiToken, setTesApiToken] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');
  const { tenantId } = useTenant();

  useEffect(() => {
    fetchProgram();
  }, [id, tenantId]);

  useEffect(() => {
    if (program && program.status !== 'draft') {
      fetchAnalytics();
    }
  }, [program, period]);

  const fetchProgram = async () => {
    try {
      const response = await tenantFetch(`/api/programs/${id}`);
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setProgram(data);
          return;
        }
      }
      // Fallback to demo data
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError('Program not found');
      }
    } catch {
      const demoProgram = getDemoProgram(tenantId, id);
      if (demoProgram) {
        setProgram(demoProgram);
      } else {
        setError('Failed to load program');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await tenantFetch(`/api/programs/${id}/analytics?period=${period}`);
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setAnalytics(data.analytics);
          return;
        }
      }
      // Fallback to demo analytics
      setAnalytics(getDemoAnalytics(tenantId, id));
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setAnalytics(getDemoAnalytics(tenantId, id));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const connectToTES = async () => {
    if (!tesClientId.trim()) {
      setConnectError('Please enter a TES client ID');
      return;
    }
    
    setIsConnecting(true);
    setConnectError('');
    
    try {
      const response = await tenantFetch(`/api/programs/${id}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          tes_client_id: tesClientId, 
          tes_api_token: tesApiToken.trim() || undefined,
          external_system: 'tes' 
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.connected) {
        setShowConnectModal(false);
        fetchProgram(); // Refresh program data
        fetchAnalytics(); // Refresh analytics
      } else {
        setConnectError(data.error || data.details || 'Failed to connect');
      }
    } catch (err) {
      setConnectError('Connection failed');
    } finally {
      setIsConnecting(false);
    }
  };

  const updateStatus = async (newStatus: string) => {
    if (!program) return;
    setIsUpdating(true);
    try {
      const response = await tenantFetch(`/api/programs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        const data = await response.json();
        setProgram({ ...program, ...data.program });
      }
    } catch {
      console.error('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const archiveProgram = async () => {
    if (!confirm('Are you sure you want to archive this program?')) return;
    try {
      const response = await tenantFetch(`/api/programs/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        window.location.href = '/programs';
      }
    } catch {
      console.error('Failed to archive program');
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'takeback': return 'Takeback';
      case 'trade_in': return 'Trade-In';
      case 'resale': return 'Resale';
      default: return type;
    }
  };

  const getTypeIcon = (type: string, size = 32) => {
    switch (type) {
      case 'takeback': return <TakebackIcon size={size} />;
      case 'trade_in': return <TradeInIcon size={size} />;
      case 'resale': return <ResaleIcon size={size} />;
      default: return <BoxIcon size={size} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return { bg: 'rgba(0,251,169,0.1)', color: '#00FBA9' };
      case 'draft': return { bg: 'rgba(169,169,169,0.1)', color: '#A9A9A9' };
      case 'paused': return { bg: 'rgba(255,184,0,0.1)', color: '#FFB800' };
      case 'archived': return { bg: 'rgba(255,76,0,0.1)', color: '#FF4C00' };
      default: return { bg: 'rgba(169,169,169,0.1)', color: '#A9A9A9' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" 
          style={{ borderColor: '#00FBA9', borderTopColor: 'transparent' }} 
        />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="text-center py-20">
        <div className="flex justify-center mb-4">
          <AlertIcon size={48} />
        </div>
        <p style={{ color: '#A9A9A9' }}>{error || 'Program not found'}</p>
        <Link to="/programs" className="mt-4 inline-block" style={{ color: '#00FBA9' }}>
          ← Back to Programs
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm" style={{ color: '#A9A9A9' }}>
        <Link to="/programs" className="hover:underline">Programs</Link>
        <span>/</span>
        <span style={{ color: '#171717' }}>{program.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="p-3" style={{ backgroundColor: '#FAFAFA' }}>
            {getTypeIcon(program.type, 32)}
          </div>
          <div>
            <h1 className="text-2xl font-light" style={{ color: '#171717' }}>{program.name}</h1>
            <p className="mt-1" style={{ color: '#A9A9A9' }}>
              {program.description || `${getTypeLabel(program.type)} program`}
            </p>
            <div className="flex items-center gap-3 mt-3">
              <span 
                className="px-3 py-1 rounded-full text-sm"
                style={getStatusColor(program.status)}
              >
                {program.status}
              </span>
              <span className="text-sm" style={{ color: '#A9A9A9' }}>
                {getTypeLabel(program.type)}
              </span>
            </div>
          </div>
        </div>

        {/* Status Actions */}
        <div className="flex items-center gap-2">
          {program.status === 'draft' && (
            <button
              onClick={() => updateStatus('active')}
              disabled={isUpdating}
              className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: '#00FBA9', color: '#171717' }}
            >
              Activate
            </button>
          )}
          {program.status === 'active' && (
            <button
              onClick={() => updateStatus('paused')}
              disabled={isUpdating}
              className="px-4 py-2 rounded-full text-sm font-medium border"
              style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
            >
              Pause
            </button>
          )}
          {program.status === 'paused' && (
            <button
              onClick={() => updateStatus('active')}
              disabled={isUpdating}
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: '#00FBA9', color: '#171717' }}
            >
              Resume
            </button>
          )}
          <button
            onClick={archiveProgram}
            className="px-4 py-2 rounded-full text-sm font-medium border"
            style={{ borderColor: 'rgba(255,76,0,0.3)', color: '#FF4C00' }}
          >
            Archive
          </button>
        </div>
      </div>

      {/* Analytics */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium" style={{ color: '#171717' }}>Analytics</h2>
          <div className="flex items-center gap-3">
            {program.status === 'active' && (
              <span className="flex items-center gap-1.5 text-sm" style={{ color: '#00FBA9' }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#00FBA9' }}></span>
                Live
              </span>
            )}
            {program.status !== 'draft' && (
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="text-sm px-2 py-1 border rounded"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="120d">Last 120 days</option>
                <option value="all">All time</option>
              </select>
            )}
          </div>
        </div>

        {program.status === 'draft' ? (
          <div className="text-center py-8" style={{ color: '#A9A9A9' }}>
            <p className="mb-2">Activate this program to start collecting data</p>
            <button
              onClick={() => updateStatus('active')}
              disabled={isUpdating}
              className="px-4 py-2 rounded-full text-sm font-medium mt-2"
              style={{ backgroundColor: '#00FBA9', color: '#171717' }}
            >
              Activate Program
            </button>
          </div>
        ) : analyticsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" 
              style={{ borderColor: '#00FBA9', borderTopColor: 'transparent' }} 
            />
          </div>
        ) : analytics?.connected === false ? (
          <div className="text-center py-8" style={{ color: '#A9A9A9' }}>
            <p className="mb-2">{analytics.message || 'Analytics not connected'}</p>
            <p className="text-sm">Configure a TES client ID in program settings</p>
          </div>
        ) : (program.type === 'takeback' || program.type === 'trade_in') ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <AnalyticCard 
                label="Total Takebacks" 
                value={String(analytics?.metrics.total || 0)} 
              />
              <AnalyticCard 
                label="In Pipeline" 
                value={String(analytics?.metrics.inPipeline || 0)} 
              />
              <AnalyticCard 
                label="Shipped" 
                value={String(analytics?.metrics.shipped || 0)} 
              />
              <AnalyticCard 
                label="Processed" 
                value={String(analytics?.metrics.processed || 0)} 
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <AnalyticCard 
                label="Total Weight" 
                value={`${analytics?.metrics.totalWeight || 0} lbs`} 
              />
              <AnalyticCard 
                label="Products Received" 
                value={String(analytics?.metrics.totalProducts || 0)} 
              />
              <AnalyticCard 
                label="Rewards Issued" 
                value={String(analytics?.metrics.rewardsIssued || 0)} 
              />
              <AnalyticCard 
                label="Rewards Value" 
                value={`$${analytics?.metrics.rewardsAmount || 0}`} 
              />
            </div>

            {analytics?.metrics.statusBreakdown && Object.keys(analytics.metrics.statusBreakdown).length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium mb-3" style={{ color: '#171717' }}>Pipeline Status</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(analytics.metrics.statusBreakdown).map(([status, count]) => (
                    <div 
                      key={status}
                      className="px-3 py-1.5 rounded-full text-sm"
                      style={{ backgroundColor: '#FAFAFA' }}
                    >
                      <span style={{ color: '#A9A9A9' }}>{status}:</span>{' '}
                      <span style={{ color: '#171717' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <AnalyticCard 
                label="Total Listings" 
                value={String(analytics?.metrics.totalListings || 0)} 
              />
              <AnalyticCard 
                label="Active Listings" 
                value={String(analytics?.metrics.activeListings || 0)} 
              />
              <AnalyticCard 
                label="Total Sales" 
                value={String(analytics?.metrics.totalSales || 0)} 
              />
              <AnalyticCard 
                label="Conversion Rate" 
                value={`${analytics?.metrics.conversionRate || 0}%`} 
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <AnalyticCard 
                label="GMV" 
                value={`$${(analytics?.metrics.gmv || 0).toLocaleString()}`} 
              />
              <AnalyticCard 
                label="Items Sold" 
                value={String(analytics?.metrics.itemsSold || 0)} 
              />
              <AnalyticCard 
                label="Shipped" 
                value={String(analytics?.metrics.itemsShipped || 0)} 
              />
              <AnalyticCard 
                label="Delivered" 
                value={String(analytics?.metrics.itemsDelivered || 0)} 
              />
            </div>

            {analytics?.metrics.statusBreakdown && Object.keys(analytics.metrics.statusBreakdown).length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium mb-3" style={{ color: '#171717' }}>Inventory Status</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(analytics.metrics.statusBreakdown).map(([status, count]) => (
                    <div 
                      key={status}
                      className="px-3 py-1.5 rounded-full text-sm"
                      style={{ backgroundColor: '#FAFAFA' }}
                    >
                      <span style={{ color: '#A9A9A9' }}>{status}:</span>{' '}
                      <span style={{ color: '#171717' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {analytics?.tesClientId && (
          <p className="text-xs mt-4" style={{ color: '#A9A9A9' }}>
            Connected to TES client: {analytics.tesClientId}
          </p>
        )}
      </div>

      {/* TES Connection (for takeback/trade_in programs) */}
      {(program.type === 'takeback' || program.type === 'trade_in') && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium" style={{ color: '#171717' }}>TES Connection</h2>
            {program.external_id ? (
              <span className="flex items-center gap-1.5 text-sm" style={{ color: '#00FBA9' }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#00FBA9' }}></span>
                Connected
              </span>
            ) : (
              <span className="text-sm" style={{ color: '#A9A9A9' }}>Not connected</span>
            )}
          </div>
          
          {program.external_id ? (
            <div className="space-y-3">
              <div className="p-4" style={{ backgroundColor: '#FAFAFA' }}>
                <p className="text-sm" style={{ color: '#A9A9A9' }}>TES Client ID</p>
                <p className="font-mono text-sm mt-1" style={{ color: '#171717' }}>{program.external_id}</p>
              </div>
              <button
                onClick={() => {
                  setTesClientId(program.external_id || '');
                  setShowConnectModal(true);
                }}
                className="text-sm"
                style={{ color: '#00FBA9' }}
              >
                Update connection
              </button>
            </div>
          ) : (
            <div className="text-center py-6" style={{ color: '#A9A9A9' }}>
              <p className="mb-3">Connect to a Takeback Event System client to sync analytics</p>
              <button
                onClick={() => setShowConnectModal(true)}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{ backgroundColor: '#00FBA9', color: '#171717' }}
              >
                Connect to TES
              </button>
            </div>
          )}
        </div>
      )}

      {/* Agentic Commerce Connection (for resale programs) */}
      {program.type === 'resale' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium" style={{ color: '#171717' }}>Agentic Commerce</h2>
            {program.external_id && program.external_system === 'agentic' ? (
              <span className="flex items-center gap-1.5 text-sm" style={{ color: '#00FBA9' }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#00FBA9' }}></span>
                Connected
              </span>
            ) : (
              <span className="text-sm" style={{ color: '#A9A9A9' }}>Not connected</span>
            )}
          </div>
          
          {program.external_id && program.external_system === 'agentic' ? (
            <div className="space-y-3">
              <div className="p-4" style={{ backgroundColor: '#FAFAFA' }}>
                <p className="text-sm" style={{ color: '#A9A9A9' }}>Program ID</p>
                <p className="font-mono text-sm mt-1" style={{ color: '#171717' }}>{program.external_id}</p>
              </div>
              <p className="text-xs" style={{ color: '#A9A9A9' }}>
                Connected to Agentic Commerce resale marketplace
              </p>
            </div>
          ) : (
            <div className="text-center py-6" style={{ color: '#A9A9A9' }}>
              <p className="mb-3">Connect to Agentic Commerce to enable the resale marketplace</p>
              <button
                onClick={async () => {
                  try {
                    const response = await tenantFetch(`/api/programs/${id}/resale-connect`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ create_new: true }),
                    });
                    if (response.ok) {
                      fetchProgram();
                    }
                  } catch (err) {
                    console.error('Failed to connect:', err);
                  }
                }}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{ backgroundColor: '#00FBA9', color: '#171717' }}
              >
                Enable Resale Marketplace
              </button>
            </div>
          )}
        </div>
      )}

      {/* Configuration */}
      <div className="card">
        <h2 className="text-base font-medium mb-4" style={{ color: '#171717' }}>Configuration</h2>
        
        {Object.keys(program.config).length === 0 ? (
          <div className="text-center py-8" style={{ color: '#A9A9A9' }}>
            <p className="mb-2">No configuration set</p>
            <button
              className="px-4 py-2 rounded-full text-sm font-medium border"
              style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
            >
              Configure Program
            </button>
          </div>
        ) : (
          <pre className="p-4 text-sm overflow-x-auto" style={{ backgroundColor: '#FAFAFA' }}>
            {JSON.stringify(program.config, null, 2)}
          </pre>
        )}
      </div>

      {/* Rules */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium" style={{ color: '#171717' }}>Business Rules</h2>
          <button
            className="text-sm"
            style={{ color: '#00FBA9' }}
          >
            + Add Rule
          </button>
        </div>
        
        {program.rules.length === 0 ? (
          <div className="text-center py-8" style={{ color: '#A9A9A9' }}>
            <p className="mb-2">No rules configured</p>
            <p className="text-sm">Add rules to automate pricing, eligibility, and routing</p>
          </div>
        ) : (
          <div className="space-y-2">
            {program.rules.map((rule) => (
              <div 
                key={rule.id}
                className="p-3 border"
                style={{ borderColor: 'rgba(169,169,169,0.2)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium" style={{ color: '#171717' }}>{rule.name}</span>
                    <span className="ml-2 text-xs px-2 py-0.5 rounded" 
                      style={{ backgroundColor: 'rgba(169,169,169,0.1)', color: '#A9A9A9' }}>
                      {rule.rule_type}
                    </span>
                  </div>
                  <span 
                    className="text-xs"
                    style={{ color: rule.is_active ? '#00FBA9' : '#A9A9A9' }}
                  >
                    {rule.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Metadata */}
      <div className="text-xs" style={{ color: '#A9A9A9' }}>
        <p>Created: {new Date(program.created_at).toLocaleString()}</p>
        <p>Last updated: {new Date(program.updated_at).toLocaleString()}</p>
        <p>ID: {program.id}</p>
      </div>

      {/* TES Connect Modal */}
      {showConnectModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => e.target === e.currentTarget && setShowConnectModal(false)}
        >
          <div 
            className="w-full max-w-md mx-4 p-6"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light" style={{ color: '#171717' }}>
                Connect to TES
              </h2>
              <button onClick={() => setShowConnectModal(false)} style={{ color: '#A9A9A9' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>
                  TES Client ID
                </label>
                <input
                  type="text"
                  value={tesClientId}
                  onChange={(e) => setTesClientId(e.target.value)}
                  placeholder="e.g., lululemon-live, brand-prod"
                  className="w-full px-4 py-3 border focus:outline-none focus:ring-2 font-mono text-sm"
                  style={{ 
                    borderColor: connectError ? '#FF4C00' : 'rgba(169,169,169,0.3)',
                    backgroundColor: '#FFFFFF',
                  }}
                  autoFocus
                />
                <p className="text-xs mt-2" style={{ color: '#A9A9A9' }}>
                  Enter the client ID configured in TES for this program
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#171717' }}>
                  TES API Token <span style={{ color: '#A9A9A9' }}>(optional)</span>
                </label>
                <input
                  type="password"
                  value={tesApiToken}
                  onChange={(e) => setTesApiToken(e.target.value)}
                  placeholder="tes_..."
                  className="w-full px-4 py-3 border focus:outline-none focus:ring-2 font-mono text-sm"
                  style={{ 
                    borderColor: 'rgba(169,169,169,0.3)',
                    backgroundColor: '#FFFFFF',
                  }}
                />
                <p className="text-xs mt-2" style={{ color: '#A9A9A9' }}>
                  Required for fetching analytics. Create via TES GraphQL: <code style={{ fontSize: '11px' }}>createOAuthToken</code>
                </p>
              </div>

              {connectError && (
                <p className="text-sm" style={{ color: '#FF4C00' }}>{connectError}</p>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setShowConnectModal(false)}
                  className="flex-1 py-3 rounded-full text-sm font-medium border"
                  style={{ 
                    borderColor: 'rgba(169,169,169,0.3)',
                    color: '#171717',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={connectToTES}
                  disabled={isConnecting || !tesClientId.trim()}
                  className="flex-1 py-3 rounded-full text-sm font-medium disabled:opacity-50"
                  style={{ 
                    backgroundColor: '#00FBA9',
                    color: '#171717',
                  }}
                >
                  {isConnecting ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4" style={{ backgroundColor: '#FAFAFA' }}>
      <p className="text-sm" style={{ color: '#A9A9A9' }}>{label}</p>
      <p className="text-2xl font-light mt-1" style={{ color: '#171717' }}>{value}</p>
    </div>
  );
}

// Demo data functions
function getDemoProgram(tenantId: string, programId: string): Program | null {
  const programs = getDemoPrograms(tenantId);
  return programs.find(p => p.id === programId) || null;
}

function getDemoPrograms(tenantId: string): Program[] {
  if (tenantId === 'arcteryx') {
    return [
      {
        id: 'prg-arc-1',
        name: "Arc'teryx ReBIRD",
        description: "Trade in your used Arc'teryx gear for store credit",
        type: 'trade_in',
        status: 'active',
        config: {
          name: "Arc'teryx ReBIRD Trade-In",
          description: "Trade in your used Arc'teryx gear for store credit",
          eligible_products: { brands: ["Arc'teryx"], any_product: false },
          conditions: { minimum_grade: 'C', accepted_grades: ['A', 'B+', 'B', 'B-', 'C+', 'C'] },
          collection: {
            mail_in: { enabled: true, prepaid_label: true, carrier: 'UPS' },
            in_store_drop_off: { enabled: true, stores: ['Vancouver', 'Seattle', 'NYC'] },
          },
          valuation: {
            method: 'condition_based',
            condition_multipliers: { excellent: 0.45, good: 0.30, fair: 0.15, poor: 0.05 },
          },
          payout: { store_credit: { enabled: true, bonus_percentage: 20 } },
          routing: {
            resell: { enabled: true, conditions: ['A', 'B+'] },
            refurbish: { enabled: true, conditions: ['B', 'B-'] },
            recycle: { enabled: true, is_default: true },
            priority_order: ['resell', 'refurbish', 'recycle'],
          },
        },
        external_system: 'tes',
        external_id: 'arcteryx-rebird-prod',
        created_at: '2024-01-15T10:00:00Z',
        updated_at: '2024-12-01T15:30:00Z',
        rules: [
          { id: 'rule-1', rule_type: 'condition', name: 'Excellent Grade Bonus', conditions: { grade: 'A' }, actions: { bonus: 10 }, priority: 1, is_active: 1 },
          { id: 'rule-2', rule_type: 'routing', name: 'Route to Resale', conditions: { grade: ['A', 'B+'] }, actions: { destination: 'resale' }, priority: 2, is_active: 1 },
        ],
      },
      {
        id: 'prg-arc-2',
        name: "Arc'teryx Repair Program",
        description: 'Send in damaged gear for professional repair',
        type: 'takeback',
        status: 'draft',
        config: {
          name: "Arc'teryx Repair & Return",
          description: 'Send in damaged gear for professional repair',
          eligible_products: { brands: ["Arc'teryx"], any_product: false },
          conditions: { minimum_grade: 'D' },
          collection: { mail_in: { enabled: true, prepaid_label: true } },
          takeback: { incentive: 'voucher', voucher_amount: 25 },
        },
        external_system: null,
        external_id: null,
        created_at: '2024-06-01T10:00:00Z',
        updated_at: '2024-11-15T09:00:00Z',
        rules: [],
      },
    ];
  }
  
  // Lululemon default
  return [
    {
      id: 'prg-lulu-1',
      name: 'Like New Trade-In',
      description: 'Trade in your gently used lululemon gear',
      type: 'trade_in',
      status: 'active',
      config: {
        name: 'Like New Trade-In',
        description: 'Trade in your gently used lululemon gear',
        eligible_products: { brands: ['lululemon'], any_product: false },
        conditions: { minimum_grade: 'B' },
        valuation: { method: 'condition_based' },
        payout: { store_credit: { enabled: true, bonus_percentage: 15 } },
      },
      external_system: 'tes',
      external_id: 'lululemon-likenew-prod',
      created_at: '2024-01-01T10:00:00Z',
      updated_at: '2024-12-01T10:00:00Z',
      rules: [],
    },
  ];
}

function getDemoAnalytics(tenantId: string, programId: string): ProgramAnalytics {
  if (tenantId === 'arcteryx' && programId === 'prg-arc-1') {
    return {
      connected: true,
      tesClientId: 'arcteryx-rebird-prod',
      metrics: {
        total: 156,
        inPipeline: 23,
        shipped: 45,
        delivered: 42,
        processed: 89,
        totalWeight: 234.5,
        totalProducts: 312,
        rewardsIssued: 134,
        rewardsAmount: 18650,
        statusBreakdown: {
          'pending-shipment': 8,
          'in-transit': 15,
          'received': 12,
          'grading': 8,
          'completed': 89,
          'declined': 24,
        },
      },
    };
  }
  
  // Default analytics
  return {
    connected: true,
    tesClientId: 'demo-client',
    metrics: {
      total: 89,
      inPipeline: 12,
      shipped: 28,
      delivered: 26,
      processed: 45,
      totalWeight: 145.2,
      totalProducts: 178,
      rewardsIssued: 78,
      rewardsAmount: 9450,
      statusBreakdown: {
        'pending-shipment': 5,
        'in-transit': 7,
        'received': 8,
        'grading': 5,
        'completed': 45,
        'declined': 19,
      },
    },
  };
}
